import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { ArrowLeft, ArrowRight, Send, User, Loader2, Lock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TestPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { isAuthenticated, getAuthHeader } = useAuth();
  
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  // Check if user has paid for access
  useEffect(() => {
    const checkAccess = async () => {
      if (!isAuthenticated) {
        setCheckingAccess(false);
        return;
      }
      
      try {
        const userRes = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
        if (userRes.data.package_status === 'active' || userRes.data.is_premium) {
          setHasAccess(true);
        }
      } catch (e) {
        console.error('Error checking access:', e);
      } finally {
        setCheckingAccess(false);
      }
    };
    checkAccess();
  }, [isAuthenticated, getAuthHeader]);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const response = await axios.get(`${API}/tests/${testId}?language=${language}`);
        setTest(response.data);
        // Load saved answers from localStorage
        const saved = localStorage.getItem(`test_${testId}_answers`);
        if (saved) {
          setAnswers(JSON.parse(saved));
        }
      } catch (error) {
        console.error('Error fetching test:', error);
        toast.error(t('common.error'));
        navigate('/tests');
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [testId, language, navigate, t]);

  // Save answers to localStorage
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem(`test_${testId}_answers`, JSON.stringify(answers));
    }
  }, [answers, testId]);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: parseInt(value) }));
  };

  const handleNext = () => {
    if (currentQuestion < test.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < test.questions.length) {
      toast.error(language === 'it' ? 'Per favore rispondi a tutte le domande' : 'Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/tests/${testId}/submit`,
        { test_id: testId, answers, language },
        { headers: getAuthHeader() }
      );
      
      // Clear saved answers
      localStorage.removeItem(`test_${testId}_answers`);
      
      // Navigate to confirmation page (not results)
      navigate('/app/test-completato', { 
        state: { 
          testName: test?.name?.[language] || test?.name?.it || 'Test',
          sentToSpecialist: response.data.sent_to_specialist 
        }
      });
    } catch (error) {
      console.error('Error submitting test:', error);
      toast.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || checkingAccess) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  // Block access if user hasn't paid
  if (!hasAccess) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-background" data-testid="test-page-locked">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-primary" />
            </div>
            
            <h1 className="text-2xl font-serif font-bold text-primary mb-4">
              {language === 'it' ? 'Accesso Riservato' : 'Access Restricted'}
            </h1>
            
            <p className="text-muted-foreground mb-8">
              {language === 'it' 
                ? 'Per accedere ai test psicologici è necessario acquistare la Valutazione Completa. Include tutti i test, un colloquio iniziale e la restituzione dei risultati con un professionista.'
                : 'To access the psychological tests, you need to purchase the Complete Assessment. It includes all tests, an initial consultation, and results review with a professional.'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="btn-gold rounded-xl"
                onClick={() => navigate('/pricing')}
                data-testid="go-to-pricing-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                {language === 'it' ? 'Acquista — €199' : 'Buy Now — €199'}
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="rounded-xl"
                onClick={() => navigate('/tests')}
              >
                {language === 'it' ? 'Torna al Catalogo' : 'Back to Catalog'}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!test) return null;

  const question = test.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / test.questions.length) * 100;
  const isLastQuestion = currentQuestion === test.questions.length - 1;
  const currentAnswer = answers[question.id];

  return (
    <div className="min-h-screen pt-20 pb-16 bg-background" data-testid="test-page">
      {/* Header */}
      <div className="glass border-b border-white/20 sticky top-16 z-40 py-4">
        <div className="container-narrow">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate('/tests')}
              className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              data-testid="back-to-tests-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t('common.back')}</span>
            </button>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {isAuthenticated ? (
                <span className="flex items-center gap-1 text-success">
                  <User className="w-4 h-4" />
                  {language === 'it' ? 'Salvato' : 'Saved'}
                </span>
              ) : (
                <span className="text-muted-foreground">{t('test.anonymous')}</span>
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium text-primary">{test.short_name}</span>
              <span className="text-muted-foreground">
                {t('test.question')} {currentQuestion + 1} {t('test.of')} {test.questions.length}
              </span>
            </div>
            <Progress value={progress} className="h-2" data-testid="test-progress" />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="container-narrow py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="bg-white border-0 shadow-medium rounded-3xl overflow-hidden" data-testid="question-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl sm:text-2xl font-serif text-primary leading-relaxed" data-testid="question-text">
                  {question.text}
                </CardTitle>
                {question.subscale && (
                  <span className="inline-block mt-2 text-xs font-mono text-muted-foreground bg-secondary px-2 py-1 rounded-full">
                    {question.subscale}
                  </span>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <RadioGroup
                  value={currentAnswer?.toString()}
                  onValueChange={(value) => handleAnswer(question.id, value)}
                  className="space-y-3"
                  data-testid="answer-options"
                >
                  {test.options.map((option, index) => (
                    <motion.div
                      key={option.value}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Label
                        htmlFor={`option-${option.value}`}
                        className={`radio-option flex items-center gap-4 ${
                          currentAnswer === option.value ? 'selected' : ''
                        }`}
                        data-testid={`option-${option.value}`}
                      >
                        <RadioGroupItem
                          value={option.value.toString()}
                          id={`option-${option.value}`}
                          className="border-2 border-primary/30 text-primary"
                        />
                        <span className="text-base font-medium text-foreground">
                          {option.label}
                        </span>
                      </Label>
                    </motion.div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            className="rounded-full px-6"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            data-testid="prev-question-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('test.previous')}
          </Button>

          {isLastQuestion ? (
            <Button
              className="rounded-full px-8 bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={handleSubmit}
              disabled={submitting || currentAnswer === undefined}
              data-testid="submit-test-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {t('test.submit')}
            </Button>
          ) : (
            <Button
              className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleNext}
              disabled={currentAnswer === undefined}
              data-testid="next-question-btn"
            >
              {t('test.next')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Question Dots */}
        <div className="flex justify-center flex-wrap gap-2 mt-8">
          {test.questions.map((q, index) => (
            <button
              key={q.id}
              onClick={() => setCurrentQuestion(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentQuestion
                  ? 'bg-primary w-6'
                  : answers[q.id] !== undefined
                  ? 'bg-accent'
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
              data-testid={`question-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TestPage;
